/**
 * VariationAnalysisResult component displays variation analysis results in a data grid format.
 * This component handles loading variation analyses, their results, and displaying them in an
 * interactive data grid with grouping, filtering, and export capabilities.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "hooks/useAuth";
import { osirisApi } from "services/api/osiris";
import ResultGrid from "components/common/pyramid/ResultGrid";
import { useGroupedItems } from "hooks/useGroupedItems";
import { useFormatConverter } from "hooks/useFormatConverter";

/**
 * VariationAnalysisResult component for displaying and interacting with variation analysis results.
 * Uses the common ResultGrid component to display data with advanced filtering,
 * grouping, and export capabilities.
 */
const VariationAnalysisResult = () => {
  /**
   * State management for the component
   */
  const [variationAnalyses, setVariationAnalyses] = useState([]); // List of available variation analyses
  const [selectedVariationAnalysis, setSelectedVariationAnalysis] =
    useState(null); // Currently selected variation analysis ID
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial data fetch
  const [isLoadingData, setIsLoadingData] = useState(false); // Loading state for grid data
  const [gridData, setGridData] = useState([]); // Data to be displayed in the grid
  const [headers, setHeaders] = useState({}); // Headers configuration for the grid
  const [expandedHeaders, setExpandedHeaders] = useState({}); // Tracks which header groups are expanded
  const [flatHeaders, setFlatHeaders] = useState([]); // Flat list of headers for format conversion
  const [toDate, setToDate] = useState([]); // Data for the excel export
  const [description, setDescription] = useState([]); // Data for the excel export
  const { accessToken, userList, companyList } = useAuth(); // Authentication token from context
  const api = osirisApi(); // API service for data fetching

  // Grouping variation analyses by user
  const groupedVariationAnalyses = useGroupedItems(
    variationAnalyses,
    userList,
    companyList
  );

  // Convert headers formats using the hook
  const convertedColumns = useFormatConverter(flatHeaders);

  // Create a map of fieldName -> converted format for easy lookup
  const formatMap = useMemo(() => {
    const map = {};
    convertedColumns.forEach((col) => {
      map[col.dataField] = {
        format: col.format,
        dataType: col.dataType,
      };
    });
    return map;
  }, [convertedColumns]);

  /**
   * Effect to load header configurations from the API
   * This determines the structure of columns in the data grid
   */
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        if (!accessToken) return;

        const data = await api.getResultHeaderVariationAnalysis();

        if (data) {
          const headers = Array.isArray(data) ? data : data.result || [];

          // Store flat headers for format conversion
          setFlatHeaders(headers);

          // Group headers by parent caption
          const groupedHeaders = headers.reduce((acc, header) => {
            const parentCaption = header.parentHeader.caption;
            if (!acc[parentCaption]) {
              acc[parentCaption] = {
                headers: [],
                canBeExpanded: header.parentHeader.canBeExpanded,
                rank: header.parentHeader.rank,
              };
            }
            acc[parentCaption].headers.push(header);
            return acc;
          }, {});

          // Ordering columns within each group by rank
          Object.keys(groupedHeaders).forEach((parentCaption) => {
            groupedHeaders[parentCaption].headers.sort((a, b) => {
              const rankA = a.rank || 0;
              const rankB = b.rank || 0;
              return rankA - rankB;
            });
          });

          setHeaders(groupedHeaders);

          // Initialize expanded state for header groups
          const initialExpandedState = Object.keys(groupedHeaders).reduce(
            (acc, key) => {
              acc[key] = false;
              return acc;
            },
            {}
          );
          setExpandedHeaders(initialExpandedState);
        }
      } catch (error) {
        console.error("Error loading headers:", error);
      }
    };

    fetchHeaders();
  }, [accessToken]);

  /**
   * Effect to load available variation analyses from the API
   * No default computation is selected at startup
   */
  useEffect(() => {
    const fetchVariationAnalyses = async () => {
      try {
        if (!accessToken) return;

        const data = await api.getVariations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const analyses = Array.isArray(data) ? data : data.result || [];
          // Sort variation analyses by creation date (newest first)
          const sortedVariationAnalyses = [...analyses].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setVariationAnalyses(sortedVariationAnalyses);
          // No automatic selection of variation analysis
        }
      } catch (error) {
        console.error("Error retrieving variation analyses:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVariationAnalyses();
  }, [accessToken]);

  /**
   * Effect to load results for the selected variation analysis
   * Triggered when a new variation analysis is selected
   */
  useEffect(() => {
    const fetchResults = async () => {
      if (!selectedVariationAnalysis || !accessToken) return;
      setIsLoadingData(true);
      try {
        const rawData = await api.getSelectedVariationAnalysis(
          selectedVariationAnalysis
        );

        const variationAnalysis = await api.getVariationAnalysis({
          id: selectedVariationAnalysis,
          includeUserTransitiveGroups: true,
        });

        try {
          const parsedVariationAnalysis = JSON.parse(variationAnalysis);
          const analysisResult = Array.isArray(parsedVariationAnalysis)
            ? parsedVariationAnalysis[0]
            : parsedVariationAnalysis.result || {};

          const description =
            analysisResult?.description ||
            parsedVariationAnalysis?.result?.description ||
            parsedVariationAnalysis?.description ||
            "Description not available";

          setDescription(description);
        } catch (variationAnalysisParseError) {
          console.error(
            "Error parsing variation analysis:",
            variationAnalysisParseError
          );
          setDescription("Description not available");
        }

        try {
          const jsonData = JSON.parse(rawData);
          const dataArray = Array.isArray(jsonData)
            ? jsonData
            : jsonData.result || [];
          setGridData(dataArray);
          setToDate("");
        } catch (parseError) {
          console.error("Error parsing JSON:", parseError);
          setGridData([]);
          setToDate("");
        }
      } catch (error) {
        console.error("Error retrieving results:", error);
        setGridData([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchResults();
  }, [selectedVariationAnalysis, accessToken]);

  /**
   * Toggles the expansion state of a header group
   * @param {string} parentCaption - The caption of the parent header
   */
  const toggleHeaderExpansion = (parentCaption) => {
    setExpandedHeaders((prev) => ({
      ...prev,
      [parentCaption]: !prev[parentCaption],
    }));
  };

  return (
    <ResultGrid
      items={variationAnalyses}
      groupedItems={groupedVariationAnalyses}
      selectedItemId={selectedVariationAnalysis}
      onItemChange={setSelectedVariationAnalysis}
      isLoading={isLoading}
      isLoadingData={isLoadingData}
      gridData={gridData}
      headers={headers}
      expandedHeaders={expandedHeaders}
      toggleHeaderExpansion={toggleHeaderExpansion}
      setExpandedHeaders={setExpandedHeaders}
      formatMap={formatMap}
      keyExprField="FifoKeyDeparture"
      itemSelectLabel="Choose your variation analysis"
      resultType="Variation Analysis"
      toDate={toDate}
      description={description}
      grouped={true}
      moduleName="OSIRIS"
    />
  );
};

export default VariationAnalysisResult;
